/* eslint-disable import/no-unused-modules */
import { useEffect } from "react";
import { useLazyGetCaasInfoQuery, useUpdateCaasI9Mutation } from "../../reduxStore/api/apiSlice";
import { useGetApplicationId } from "./useGetApplicationId";
import { isSalesforceApplication } from "../../helpers/url-params";
import { getCountryForDomain } from "../../utility/locale-helper";
import { useAppSelector } from "../../reduxStore/reduxHooks";
import { getConfigData, getFeatureFlag, setConfigData } from "../../reduxStore/slices/configSlice";
import { useDispatch } from "react-redux";
import { CONFIG_DATA_KEY } from "../../utility/enums/common";
import { useGetCandidateData } from "./useGetCandidateData";
import { CAAS_DRUGTEST_ALLOWED_COUNTRY, FeatFlag } from "../../config/appConfigConstants";
import { isDevEnv } from "../../utility/dev-env-helpers";
import { useGetAllContigenciesData } from "./useGetAllContigenciesData";
import { isDisableCaasI9GETCallEnabledByAppConfig, isTaskEnabledByAppConfig } from "../../utility/app-config-helper";
import { CAAS_STATUS } from "@amzn/hvh-simple-hire-checklist-internal-api-common";

export const useGetCaasI9Status = () => {
  const { caasI9Status } = useAppSelector(getConfigData);
  const { isAshConfigLoaded } = useAppSelector(getFeatureFlag);
  const dispatch = useDispatch();
  const allContigencies = useGetAllContigenciesData();
  const applicationId = useGetApplicationId();
  const candidateData = useGetCandidateData();
  const [updateCaasI9] = useUpdateCaasI9Mutation();
  const [getCaasI9Status] = useLazyGetCaasInfoQuery();

  useEffect(() => {
    const country = getCountryForDomain();
    const skipCallForGetCaasI9Status =
      isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_WORK_AUTH, applicationId) ||
      !(country in CAAS_DRUGTEST_ALLOWED_COUNTRY && candidateData);

    if (candidateData?.candidateId && isAshConfigLoaded) {
      const flagEnabled = isTaskEnabledByAppConfig(FeatFlag.ENABLE_ALL_CONTINGENCIES_WORK_AUTH, applicationId);
      if (flagEnabled && allContigencies !== "SKIPPED" && allContigencies?.WorkAuthorization) {
        if (
          allContigencies?.WorkAuthorization.status === CAAS_STATUS.WaitingForASHToCreateOrder &&
          !isDisableCaasI9GETCallEnabledByAppConfig()
        ) {
          // This skip condition was added to only called to void another api call
          if (caasI9Status && (caasI9Status as unknown as string) !== "SKIPPED") return;
          updateCaasI9({
            candidateId: candidateData?.candidateId,
            redirectURL: encodeURIComponent(window.location.href),
          })
            .unwrap()
            .then((i9Response) => {
              dispatch(
                setConfigData({
                  key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
                  value: i9Response,
                })
              );
            });
        } else {
          dispatch(
            setConfigData({
              key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
              value: allContigencies.WorkAuthorization,
            })
          );
        }
      } else if ((!flagEnabled || isSalesforceApplication(applicationId)) && !skipCallForGetCaasI9Status) {
        getCaasI9Status(
          {
            candidateId: candidateData?.candidateId,
            redirectUrl: encodeURIComponent(window.location.href),
          },
          true
        )
          .unwrap()
          .then((data) => {
            dispatch(
              setConfigData({
                key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
                value: data,
              })
            );
          });
      } else {
        if (isDevEnv()) console.warn("Skipping useGetCaasI9Status hook");
        dispatch(
          setConfigData({
            key: CONFIG_DATA_KEY.CAAS_I9_STATUS,
            value: "SKIPPED",
          })
        );
      }
    }
  }, [
    isAshConfigLoaded,
    caasI9Status,
    allContigencies,
    applicationId,
    dispatch,
    getCaasI9Status,
    candidateData?.candidateId,
    candidateData,
    updateCaasI9,
  ]);

  return caasI9Status;
};
